/**
 * Theme Configuration
 * Centralized color palette and design tokens
 * Usage: import { getTheme } from '@/config/theme';
 *        const theme = getTheme(mode); // mode: "light" | "dark"
 */

export const LIGHT_THEME = {
  colors: {
    // Primary Brand Colors
    primary: '#0222F1',
    primaryDark: '#0115A8',
    primaryLight: '#4043F8',

    // Backgrounds
    background: '#F5F5F5',
    secondary: '#F8FFFF',
    card: '#FFFFFF',

    // Text
    text: '#0F172A',
    textMuted: '#64748B',
    textLight: '#94A3B8',

    // Borders
    border: 'rgba(2,34,241,0.10)',
    borderDark: 'rgba(2,34,241,0.20)',

    // Status Colors
    success: '#16A34A',
    successAlt: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    info: '#0222F1',

    // Secondary Accents
    purple: '#7C3AED',

    // Semantic backgrounds
    successBg: 'rgba(22,163,74,0.10)',
    warningBg: 'rgba(245,158,11,0.10)',
    dangerBg: 'rgba(239,68,68,0.12)',
    infoBg: 'rgba(2,34,241,0.08)',
    purpleBg: 'rgba(124,58,237,0.10)',
  },

  shadows: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    base: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    md: '0 10px 35px rgba(2, 34, 241, 0.05)',
    lg: '0 12px 40px rgba(2, 34, 241, 0.05)',
    xl: '0 18px 45px rgba(15, 23, 42, 0.14)',
    xxl: '0 20px 80px rgba(2, 34, 241, 0.08)',
  },

  borderRadius: {
    sm: '8px',
    base: '12px',
    md: '16px',
    lg: '24px',
    xl: '28px',
    '2xl': '32px',
    full: '9999px',
  },

  animations: {
    gradientShift: '10s ease infinite',
    floatPulse: '2.4s ease-in-out infinite',
    glowRing: '2.8s ease-in-out infinite',
    textBlink: '1.6s ease-in-out infinite',
  },
};

export const DARK_THEME = {
  ...LIGHT_THEME,
  colors: {
    ...LIGHT_THEME.colors,
    // Primary Brand Colors
    primary: '#5B6CFA',
    primaryDark: '#4043F8',
    primaryLight: '#8A93FF',

    // Backgrounds
    background: '#0B1220',
    secondary: '#0F172A',
    card: '#111A2E',

    // Text
    text: '#F1F5F9',
    textMuted: '#94A3B8',
    textLight: '#64748B',

    // Borders
    border: 'rgba(148,163,184,0.16)',
    borderDark: 'rgba(148,163,184,0.28)',

    // Status Colors
    success: '#22C55E',
    successAlt: '#34D399',
    warning: '#FBBF24',
    danger: '#F87171',
    info: '#5B6CFA',

    // Secondary Accents
    purple: '#A78BFA',

    // Semantic backgrounds
    successBg: 'rgba(34,197,94,0.14)',
    warningBg: 'rgba(251,191,36,0.14)',
    dangerBg: 'rgba(248,113,113,0.16)',
    infoBg: 'rgba(91,108,250,0.14)',
    purpleBg: 'rgba(167,139,250,0.14)',
  },
  shadows: {
    ...LIGHT_THEME.shadows,
    md: '0 10px 35px rgba(0, 0, 0, 0.25)',
    lg: '0 12px 40px rgba(0, 0, 0, 0.3)',
    xl: '0 18px 45px rgba(0, 0, 0, 0.4)',
    xxl: '0 20px 80px rgba(0, 0, 0, 0.35)',
  },
};

export type ThemeMode = 'light' | 'dark';

export const getTheme = (mode: ThemeMode) => (mode === 'dark' ? DARK_THEME : LIGHT_THEME);

// Backward-compatible default export (light palette).
export const THEME = LIGHT_THEME;

export default THEME;

